import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PickupOptionsPublic, ProductDto } from "@gaole/shared";
import { validatePickupScheduledAt } from "@gaole/shared";
import { StorePickupSection } from "../components/pickup/StorePickupSection";
import { api, ApiError } from "../lib/api";
import { rm, credits, idemKey } from "../lib/format";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button, Input, PageLoader } from "../components/ui";
import { CountrySelect } from "../components/CountrySelect";
import { PhoneInput } from "../components/PhoneInput";
import { AddressRegionField, formatAddressLocation } from "../components/AddressRegionField";
import { shippingLocationQuery } from "../lib/shipping";
import { emptyAddressForm, patchAddressForm, type AddressFormFields } from "../lib/addressForm";
import { PageHeader } from "../components/storefront/PageHeader";
import { BankInTransferDetails } from "../components/payments/BankInTransferDetails";
import { PaymentMethodPicker } from "../components/payments/PaymentMethodPicker";
import { productImageSrc } from "../components/ProductCard";
import { PolicyConsentCheckbox } from "../components/PolicyConsentCheckbox";
import { MobileStickyBar, MOBILE_STICKY_BAR_PAD } from "../components/shell/MobileStickyBar";
import { openPaymentGateway, type GkashFormPayload } from "../lib/payments";
import { OrderItemThumb } from "../components/storefront/OrderItemThumb";
import {
  PAYMENT_METHOD_FLEXXI,
  paymentMethodLabel,
  primaryPayButtonLabel,
  sortPaymentMethods,
} from "../lib/paymentMethods";
import { ListingTitle } from "../components/ListingTitle";
import { FlexxiShopPanel } from "../components/shop/FlexxiShopPanel";
import { pickFlexxiCartLine } from "../lib/flexxiShop";
import { marketplaceShopPath } from "../lib/marketplaceShopPath";

interface AddressDto {
  id: string;
  label: string;
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  isDefault: boolean;
}

interface CartPackLine {
  id: string;
  quantity: number;
  pack: { id: string; name: string; images: string[] };
  cashTotal: number;
}

type MarketplaceCheckoutGroup = {
  sellerId: string;
  shopName: string;
  shopSlug: string;
  items: { quantity: number; listing: { id: string; title: string; price: number; images: string[] } }[];
  subtotal: number;
  shippingFee: number;
  total: number;
};

interface CartData {
  items: { id: string; quantity: number; product: ProductDto; discount?: number }[];
  packItems?: CartPackLine[];
  subtotal: number;
  discountTotal: number;
  shippingFee: number;
  shippingCountry?: string | null;
  shippingState?: string | null;
  fulfilmentMethod?: string;
  total: number;
  grandTotal?: number;
  marketplace?: { groups: MarketplaceCheckoutGroup[]; subtotal: number; shippingTotal: number; total: number } | null;
}

interface CheckoutResponse {
  orderId?: string;
  orderNo?: string;
  groupId?: string;
  grandTotal?: number;
  status: "PAID" | "REDIRECT" | "GKASH_FORM" | "AWAITING_RECEIPT";
  redirectUrl?: string;
  form?: GkashFormPayload;
}

type AddressForm = AddressFormFields;

function emptyAddress(displayName = ""): AddressForm {
  return emptyAddressForm(displayName);
}

type FulfilmentChoice = "DELIVERY" | "PICKUP" | "VAULT";

function productNeedsShipping(product: ProductDto): boolean {
  const behavior = product.behavior ?? product.type;
  return behavior === "SINGLE" || behavior === "BUNDLE";
}

function productIsCreditPack(product: ProductDto): boolean {
  const behavior = product.behavior ?? product.type;
  return behavior === "CREDIT_PACK";
}

export function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutStore = searchParams.get("store") === "1";
  const checkoutSellerIds = useMemo(() => {
    const raw = searchParams.get("sellers");
    if (!raw?.trim()) return null;
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [searchParams]);
  const checkoutStoreProductIds = useMemo(() => {
    if (!searchParams.has("products")) return null;
    const raw = searchParams.get("products");
    if (!raw?.trim()) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [searchParams]);
  const checkoutStorePackIds = useMemo(() => {
    if (!searchParams.has("packs")) return null;
    const raw = searchParams.get("packs");
    if (!raw?.trim()) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [searchParams]);
  const checkoutListingIds = useMemo(() => {
    if (!searchParams.has("listings")) return null;
    const raw = searchParams.get("listings");
    if (!raw?.trim()) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [searchParams]);
  const queryClient = useQueryClient();
  const { config, user, moduleEnabled } = useAuth();
  const [addressId, setAddressId] = useState<string | null>(null);
  const [newAddress, setNewAddress] = useState(() => emptyAddress(user?.displayName ?? ""));
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [method, setMethod] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  const [fulfilmentMethod, setFulfilmentMethod] = useState<FulfilmentChoice>("DELIVERY");
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [pickupScheduledAt, setPickupScheduledAt] = useState("");
  const leavingRef = useRef(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => idemKey());
  const [marketFulfilment, setMarketFulfilment] = useState<
    Record<string, { method: "DELIVERY" | "SELF_COLLECT"; addressId?: string }>
  >({});

  const { data: addresses, isLoading: addrLoading } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api<{ items: AddressDto[] }>("/addresses"),
  });

  const shippingLocation = useMemo(() => {
    if (showNewAddress) {
      return {
        country: newAddress.country || undefined,
        state: newAddress.state || undefined,
      };
    }
    const addr = addresses?.items.find((a) => a.id === addressId);
    return addr ? { country: addr.country, state: addr.state } : undefined;
  }, [showNewAddress, newAddress.country, newAddress.state, addressId, addresses?.items]);

  const { data: pickupOptions } = useQuery({
    queryKey: ["pickup-options"],
    queryFn: () => api<PickupOptionsPublic>("/shipping/pickup-options"),
    enabled: true,
  });

  const pickupAvailable = Boolean(
    pickupOptions?.enabled && pickupOptions.locations.length > 0,
  );
  const buyToVaultAvailable = Boolean(config?.buyToVaultEnabled);

  const { data: cart, isLoading: cartLoading } = useQuery({
    queryKey: [
      "cart",
      fulfilmentMethod,
      shippingLocation?.country ?? "",
      shippingLocation?.state ?? "",
    ],
    queryFn: () =>
      api<CartData>(
        `/cart${shippingLocationQuery({
          ...shippingLocation,
          fulfilmentMethod,
        })}`,
      ),
  });

  const { data: wallet } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api<{ balance: number }>("/wallet").catch(() => ({ balance: 0 })),
    enabled: Boolean(config?.creditsEnabled),
  });

  const allPackItems = cart?.packItems ?? [];
  const allMarketGroups = cart?.marketplace?.groups ?? [];
  const hasStoreInCart = (cart?.items.length ?? 0) > 0 || allPackItems.length > 0;
  const hasMarketInCart = allMarketGroups.length > 0;

  const scopeStore = checkoutStore || (checkoutSellerIds == null && hasStoreInCart && !hasMarketInCart);
  const scopeMarketIds =
    checkoutSellerIds ??
    (checkoutStore ? null : hasMarketInCart && !hasStoreInCart ? allMarketGroups.map((g) => g.sellerId) : null);
  let marketGroups =
    scopeMarketIds == null
      ? []
      : allMarketGroups.filter((g) => scopeMarketIds.includes(g.sellerId));
  if (checkoutListingIds != null) {
    const listingSet = new Set(checkoutListingIds);
    marketGroups = marketGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((row) => listingSet.has(row.listing.id)),
      }))
      .filter((g) => g.items.length > 0);
  }

  const storeItems =
    cart && scopeStore
      ? checkoutStoreProductIds == null
        ? cart.items
        : cart.items.filter((i) => checkoutStoreProductIds.includes(i.product.id))
      : [];
  const packItems =
    cart && scopeStore
      ? checkoutStorePackIds == null
        ? allPackItems
        : allPackItems.filter((p) => checkoutStorePackIds.includes(p.pack.id))
      : [];

  const hasStoreCart = scopeStore && (storeItems.length > 0 || packItems.length > 0);
  const hasMarketCart = marketGroups.length > 0;
  const cartEmpty = !cart || (!hasStoreCart && !hasMarketCart);
  const needsShipping = hasStoreCart && storeItems.some((i) => productNeedsShipping(i.product));
  const needsAddressSection = needsShipping || hasMarketCart;
  const creditTopupOnly =
    storeItems.length > 0 && packItems.length === 0 && storeItems.every((i) => productIsCreditPack(i.product));
  const canQuoteShipping = Boolean(
    shippingLocation?.country && (shippingLocation.country !== "MY" || shippingLocation.state),
  );
  const storeMerchandiseTotal = storeItems.reduce(
    (sum, i) => sum + i.product.price * i.quantity,
    0,
  );
  const storePackMerchandise = packItems.reduce((sum, p) => sum + p.cashTotal, 0);
  const storeDiscount = storeItems.reduce((sum, i) => {
    const full = cart?.items.find((x) => x.product.id === i.product.id);
    return sum + (full?.discount ?? 0);
  }, 0);
  const scopedMerchandiseTotal = storeMerchandiseTotal + storePackMerchandise - storeDiscount;
  const merchandiseTotal = cart ? cart.subtotal - cart.discountTotal : 0;
  const shippingQuoted =
    !needsShipping || fulfilmentMethod !== "DELIVERY" || canQuoteShipping;
  const marketSubtotal = marketGroups.reduce(
    (s, g) => s + g.items.reduce((n, row) => n + row.listing.price * row.quantity, 0),
    0,
  );
  const marketShipping = marketGroups.reduce((s, g) => s + g.shippingFee, 0);
  const storeUsesFullCartQuote =
    checkoutStoreProductIds == null && checkoutStorePackIds == null && hasStoreCart;
  const storeTotal =
    cart && shippingQuoted && hasStoreCart
      ? storeUsesFullCartQuote
        ? cart.total
        : scopedMerchandiseTotal + (needsShipping ? cart.shippingFee : 0)
      : hasStoreCart
        ? scopedMerchandiseTotal
        : 0;
  const checkoutTotal = storeTotal + marketSubtotal + (canQuoteShipping ? marketShipping : 0);
  const creditCost = Math.ceil(checkoutTotal * (config?.creditConversionRate ?? 1));
  const flexxiLine =
    moduleEnabled("ecommerce") && storeItems.length
      ? pickFlexxiCartLine(storeItems, packItems.length)
      : null;
  const flexxiCheckoutEnabled = Boolean(
    flexxiLine && moduleEnabled("instalments") && config?.flexxiEnabled && user,
  );

  const { data: flexxiTemplatesData } = useQuery({
    queryKey: ["flexxi", "templates"],
    queryFn: () =>
      api<{
        templates: { id: string }[];
        kyc: { kycVerified: boolean };
      }>("/flexxi/templates"),
    enabled: flexxiCheckoutEnabled,
  });

  const flexxiPaymentAvailable = Boolean(
    flexxiCheckoutEnabled && flexxiTemplatesData?.templates?.length,
  );
  const flexxiKycVerified = flexxiTemplatesData?.kyc?.kycVerified ?? false;

  useEffect(() => {
    if (!hasMarketCart) return;
    setMarketFulfilment((prev) => {
      const next = { ...prev };
      for (const g of marketGroups) {
        if (!next[g.sellerId]) next[g.sellerId] = { method: "DELIVERY" };
      }
      return next;
    });
  }, [hasMarketCart, marketGroups]);

  const methods = useMemo(() => {
    const list: string[] = [];
    if (config?.creditsEnabled && !creditTopupOnly) list.push("CREDITS");
    if (config?.paymentMethods.stripe) list.push("STRIPE");
    if (config?.paymentMethods.gkash) list.push("GKASH");
    if (config?.paymentMethods.bankIn) list.push("BANK_IN");
    if (flexxiPaymentAvailable) list.push(PAYMENT_METHOD_FLEXXI);
    return sortPaymentMethods(list);
  }, [config, creditTopupOnly, flexxiPaymentAvailable]);

  useEffect(() => {
    if (
      (!pickupAvailable && fulfilmentMethod === "PICKUP") ||
      (!buyToVaultAvailable && fulfilmentMethod === "VAULT")
    ) {
      setFulfilmentMethod("DELIVERY");
    }
  }, [pickupAvailable, buyToVaultAvailable, fulfilmentMethod]);

  useEffect(() => {
    if (pickupAvailable && pickupOptions?.locations.length && !pickupLocationId) {
      setPickupLocationId(pickupOptions.locations[0].id);
    }
  }, [pickupAvailable, pickupOptions?.locations, pickupLocationId]);

  useEffect(() => {
    if (!needsShipping && !hasMarketCart) return;
    if (showNewAddress) return;
    if (addresses?.items.length && !addressId) {
      const def = addresses.items.find((a) => a.isDefault) ?? addresses.items[0];
      setAddressId(def.id);
      setShowNewAddress(false);
    }
    if (addresses && addresses.items.length === 0) {
      setShowNewAddress(true);
      setAddressId(null);
    }
  }, [addresses, addressId, needsShipping, hasMarketCart, showNewAddress]);

  useEffect(() => {
    if (!methods.length) return;
    const currentOk =
      method &&
      methods.includes(method) &&
      !(method === PAYMENT_METHOD_FLEXXI && !flexxiKycVerified) &&
      !(method === "CREDITS" && (wallet?.balance ?? 0) < creditCost);
    if (!currentOk) {
      const firstEnabled =
        methods.find(
          (m) =>
            !(m === PAYMENT_METHOD_FLEXXI && !flexxiKycVerified) &&
            !(m === "CREDITS" && (wallet?.balance ?? 0) < creditCost),
        ) ?? methods[0]!;
      setMethod(firstEnabled);
    }
  }, [methods, method, flexxiKycVerified, wallet?.balance, creditCost]);

  if (cartLoading || addrLoading) return <PageLoader />;
  if (cart && hasStoreInCart && hasMarketInCart && !checkoutStore && !checkoutSellerIds?.length) {
    if (!submitting && !leavingRef.current) {
      navigate("/cart");
      return null;
    }
  }

  if (cartEmpty && !submitting && !leavingRef.current) {
    navigate("/cart");
    return null;
  }
  if (cartEmpty) return <PageLoader />;

  const total = checkoutTotal;
  const selectedPickupLocation = pickupOptions?.locations.find((l) => l.id === pickupLocationId);

  function validateNewAddress(): string | null {
    if (!newAddress.label.trim()) return "Add a label (e.g. Home)";
    if (!newAddress.name.trim()) return "Recipient name is required";
    if (!newAddress.phone.trim()) return "Phone is required";
    if (!newAddress.address1.trim()) return "Address line 1 is required";
    if (!newAddress.postcode.trim()) return "Postcode is required";
    if (!newAddress.city.trim()) return "City is required";
    if (!newAddress.country.trim()) return "Country is required";
    if (!newAddress.state.trim()) return newAddress.country === "MY" ? "State is required" : "State or region is required";
    return null;
  }

  async function submit() {
    setSubmitting(true);
    try {
      let finalAddressId = addressId ?? undefined;
      if ((needsShipping && fulfilmentMethod === "DELIVERY" || hasMarketCart) && showNewAddress) {
        const err = validateNewAddress();
        if (err) {
          toast("error", err);
          setSubmitting(false);
          return;
        }
        const created = await api<{ address: AddressDto }>("/addresses", {
          body: {
            ...newAddress,
            address2: newAddress.address2 || null,
          },
        });
        finalAddressId = created.address.id;
        await queryClient.invalidateQueries({ queryKey: ["addresses"] });
        toast("success", "Address saved for next time");
      }
      if (needsShipping && fulfilmentMethod === "PICKUP") {
        if (!pickupLocationId || !pickupScheduledAt) {
          toast("error", "Select a pickup location and time");
          setSubmitting(false);
          return;
        }
        if (pickupOptions) {
          const pickupErr = validatePickupScheduledAt(
            new Date(pickupScheduledAt),
            new Date(),
            pickupOptions.minLeadHours,
            pickupOptions.maxDaysAhead,
            selectedPickupLocation?.availability,
          );
          if (pickupErr) {
            toast("error", pickupErr);
            setSubmitting(false);
            return;
          }
        }
      } else if ((needsShipping && fulfilmentMethod === "DELIVERY" || hasMarketCart) && !finalAddressId) {
        toast("error", "Pick or create a shipping address");
        setSubmitting(false);
        return;
      }
      if (hasMarketCart) {
        for (const g of marketGroups) {
          const row = marketFulfilment[g.sellerId] ?? { method: "DELIVERY" as const };
          if (row.method === "DELIVERY" && !(row.addressId ?? finalAddressId)) {
            toast("error", `Select a delivery address for ${g.shopName}`);
            setSubmitting(false);
            return;
          }
        }
      }

      if (!acceptPolicies) {
        toast("error", "Accept the store policies to continue");
        setSubmitting(false);
        return;
      }

      const fulfilmentPayload: Record<string, { method: "DELIVERY" | "SELF_COLLECT"; addressId?: string }> = {};
      if (hasMarketCart) {
        for (const g of marketGroups) {
          const row = marketFulfilment[g.sellerId] ?? { method: "DELIVERY" as const };
          fulfilmentPayload[g.sellerId] =
            row.method === "SELF_COLLECT"
              ? { method: "SELF_COLLECT" }
              : { method: "DELIVERY", addressId: row.addressId ?? finalAddressId };
        }
      }

      const res = await api<CheckoutResponse>("/checkout", {
        body: {
          method,
          includeStore: hasStoreCart ? true : undefined,
          storeProductIds:
            hasStoreCart && checkoutStoreProductIds != null ? checkoutStoreProductIds : undefined,
          storePackIds: hasStoreCart && checkoutStorePackIds != null ? checkoutStorePackIds : undefined,
          marketplaceSellerIds: hasMarketCart ? marketGroups.map((g) => g.sellerId) : undefined,
          marketplaceListingIds:
            hasMarketCart && checkoutListingIds != null ? checkoutListingIds : undefined,
          addressId: needsShipping && fulfilmentMethod === "DELIVERY" ? finalAddressId : undefined,
          fulfilmentMethod: needsShipping ? fulfilmentMethod : undefined,
          pickupLocationId: needsShipping && fulfilmentMethod === "PICKUP" ? pickupLocationId : undefined,
          pickupScheduledAt:
            needsShipping && fulfilmentMethod === "PICKUP" && pickupScheduledAt
              ? pickupScheduledAt
              : undefined,
          marketplaceFulfilmentBySeller: hasMarketCart ? fulfilmentPayload : undefined,
          idempotencyKey,
          acceptPolicies: true,
        },
      });

      leavingRef.current = true;

      if (res.status === "AWAITING_RECEIPT") {
        const target = res.orderId ? `/orders/${res.orderId}#bank-in-payment` : "/orders";
        navigate(target, { replace: true });
        void queryClient.invalidateQueries({ queryKey: ["cart"] });
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        return;
      }

      if (openPaymentGateway(res)) {
        void queryClient.invalidateQueries({ queryKey: ["cart"] });
        return;
      }

      if (res.status === "PAID") {
        toast("success", "Paid!");
        await queryClient.invalidateQueries({ queryKey: ["wallet"] });
        navigate(res.orderId ? `/orders/${res.orderId}` : "/orders");
        void queryClient.invalidateQueries({ queryKey: ["cart"] });
        return;
      }

      if (res.status === "REDIRECT" || res.status === "GKASH_FORM") {
        toast("error", "Couldn't open the payment gateway — try again from your order.");
      }
      navigate(res.orderId ? `/orders/${res.orderId}` : "/orders");
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
    } catch (err) {
      setIdempotencyKey(idemKey());
      toast("error", err instanceof ApiError ? err.message : "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  }

  const labelForPaymentMethod = (m: string) =>
    paymentMethodLabel(m, {
      walletBalance: wallet?.balance,
      creditConversionRate: config?.creditConversionRate,
    });

  const flexxiCheckout = method === PAYMENT_METHOD_FLEXXI;
  const amountLabel = method === "CREDITS" ? credits(creditCost) : rm(total);
  const submitLabel = submitting
    ? method === "BANK_IN"
      ? "Creating order…"
      : "Placing order…"
    : primaryPayButtonLabel(method, amountLabel);

  return (
    <div className={`space-y-5 px-4 pt-4 ${MOBILE_STICKY_BAR_PAD} md:pb-8`}>
      <PageHeader
        title="Checkout"
        description="Choose shipping, payment method, and confirm your order."
      />

      {needsAddressSection && (
        <section className="space-y-3">
          {needsShipping && (pickupAvailable || buyToVaultAvailable) && (
            <div className="flex gap-1.5 rounded-full bg-surface p-1">
              {(
                [
                  { key: "DELIVERY", label: "Delivery", show: true },
                  { key: "PICKUP", label: "Pickup", show: pickupAvailable },
                  { key: "VAULT", label: "Store in Vault", show: buyToVaultAvailable },
                ] as const
              )
                .filter((o) => o.show)
                .map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setFulfilmentMethod(o.key)}
                    className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                      fulfilmentMethod === o.key ? "bg-gradient-brand text-white" : "text-white/50"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
            </div>
          )}

          {needsShipping && fulfilmentMethod === "VAULT" ? (
            <div className="rounded-2xl border border-brand-pink/25 bg-brand-pink/[0.06] p-3.5 text-xs text-white/70">
              <p className="font-semibold text-white">Your cards go straight to your Vault</p>
              <p className="mt-1">
                No shipping fee today. Request delivery or pickup any time from Inventory — shipping is charged then.
              </p>
            </div>
          ) : null}

          {needsShipping && fulfilmentMethod === "PICKUP" && pickupOptions ? (
            <StorePickupSection
              pickupOptions={pickupOptions}
              locationId={pickupLocationId}
              onLocationIdChange={setPickupLocationId}
              scheduledAt={pickupScheduledAt}
              onScheduledAtChange={setPickupScheduledAt}
            />
          ) : null}
          {(!needsShipping || fulfilmentMethod === "DELIVERY" || hasMarketCart) && (
            <>
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            {hasMarketCart && needsShipping && fulfilmentMethod !== "DELIVERY"
              ? "Delivery address (marketplace)"
              : "Shipping address"}
          </h2>
          <p className="text-[11px] text-white/45">
            Select a saved address or create one — it will be saved to your account for next time.
          </p>

          {addresses?.items.map((addr) => (
            <button
              key={addr.id}
              type="button"
              onClick={() => {
                setAddressId(addr.id);
                setShowNewAddress(false);
              }}
              className={`w-full rounded-2xl border p-3 text-left text-xs transition-colors ${
                addressId === addr.id && !showNewAddress
                  ? "border-brand-pink bg-brand-pink/10"
                  : "border-white/5 bg-surface"
              }`}
            >
              <p className="font-semibold">
                {addr.label}
                {addr.isDefault && (
                  <span className="ml-1.5 rounded-full bg-brand-pink/20 px-1.5 py-0.5 text-[9px] font-medium text-brand-pink">
                    Default
                  </span>
                )}
              </p>
              <p className="mt-1 font-medium text-white/90">
                {addr.name} <span className="font-normal text-white/50">· {addr.phone}</span>
              </p>
              <p className="mt-0.5 text-white/60">
                {addr.address1}
                {addr.address2 ? `, ${addr.address2}` : ""},{" "}
                {formatAddressLocation(addr)}
              </p>
            </button>
          ))}

          {!showNewAddress ? (
            <button
              type="button"
              onClick={() => {
                setShowNewAddress(true);
                setAddressId(null);
                setNewAddress(emptyAddress(user?.displayName ?? ""));
              }}
              className="text-xs font-medium text-brand-pink"
            >
              + Add new address
            </button>
          ) : (
            <div className="space-y-2 rounded-2xl border border-brand-pink/30 bg-surface p-3">
              <p className="font-display text-[10px] font-semibold uppercase tracking-widest text-white/40">
                New address
              </p>
              <Input
                label="Label"
                placeholder="Home, Office…"
                value={newAddress.label}
                onChange={(e) => patchAddressForm(setNewAddress, { label: e.target.value })}
              />
              <Input
                label="Recipient name"
                placeholder="Full name"
                value={newAddress.name}
                onChange={(e) => patchAddressForm(setNewAddress, { name: e.target.value })}
              />
              <PhoneInput
                label="Phone"
                placeholder="Mobile number"
                value={newAddress.phone}
                onChange={(phone) => patchAddressForm(setNewAddress, { phone })}
                country={newAddress.country}
              />
              <Input
                label="Address line 1"
                placeholder="Street address"
                value={newAddress.address1}
                onChange={(e) => patchAddressForm(setNewAddress, { address1: e.target.value })}
              />
              <Input
                label="Address line 2"
                placeholder="Unit, floor (optional)"
                value={newAddress.address2}
                onChange={(e) => patchAddressForm(setNewAddress, { address2: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Postcode"
                  placeholder="Postcode"
                  inputMode="numeric"
                  value={newAddress.postcode}
                  onChange={(e) => patchAddressForm(setNewAddress, { postcode: e.target.value })}
                />
                <Input
                  label="City"
                  placeholder="City"
                  value={newAddress.city}
                  onChange={(e) => patchAddressForm(setNewAddress, { city: e.target.value })}
                />
              </div>
              <CountrySelect
                label="Country"
                required
                value={newAddress.country}
                onChange={(country) =>
                  patchAddressForm(setNewAddress, (prev) => ({
                    country,
                    state: country === prev.country ? prev.state : "",
                  }))
                }
              />
              <AddressRegionField
                country={newAddress.country}
                state={newAddress.state}
                onStateChange={(state) => patchAddressForm(setNewAddress, { state })}
              />
              <label className="flex items-center gap-2 pt-1 text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={newAddress.isDefault}
                  onChange={(e) => patchAddressForm(setNewAddress, { isDefault: e.target.checked })}
                  className="rounded border-white/20 bg-transparent"
                />
                Set as default for next checkout
              </label>
              <p className="text-[11px] text-white/40">Saved to your account and reusable next time.</p>
              {addresses && addresses.items.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewAddress(false);
                    const def = addresses.items.find((a) => a.isDefault) ?? addresses.items[0];
                    setAddressId(def.id);
                  }}
                  className="text-xs text-white/50"
                >
                  Use saved address instead
                </button>
              )}
            </div>
          )}
            </>
          )}
        </section>
      )}

      {hasMarketCart && (
        <section className="space-y-3">
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Marketplace sellers
          </h2>
          {marketGroups.map((group) => (
            <div key={group.sellerId} className="space-y-2 rounded-2xl border border-white/5 bg-surface p-3">
              <Link to={marketplaceShopPath(group.shopSlug)} className="font-display text-sm font-bold hover:text-brand-pink">
                {group.shopName}
              </Link>
              <ul className="space-y-1 text-[11px] text-white/60">
                {group.items.map((row, i) => (
                  <li key={i}>
                    {row.listing.title} ×{row.quantity} · {rm(row.listing.price * row.quantity)}
                  </li>
                ))}
              </ul>
              <select
                className="w-full rounded-xl border border-white/10 bg-surface-2 px-3 py-2.5 text-sm"
                value={marketFulfilment[group.sellerId]?.method ?? "DELIVERY"}
                onChange={(e) =>
                  setMarketFulfilment({
                    ...marketFulfilment,
                    [group.sellerId]: {
                      ...marketFulfilment[group.sellerId],
                      method: e.target.value as "DELIVERY" | "SELF_COLLECT",
                    },
                  })
                }
              >
                <option value="DELIVERY">Delivery</option>
                <option value="SELF_COLLECT">{config?.marketplaceCollectLabel ?? "COD"}</option>
              </select>
              {marketFulfilment[group.sellerId]?.method === "SELF_COLLECT" && (
                <p className="text-[10px] text-white/45">
                  Meet the seller in person and pay on handover. The seller&apos;s {config?.marketplaceCollectLabel ?? "COD"}{" "}
                  location appears on your order page.
                </p>
              )}
              {(marketFulfilment[group.sellerId]?.method ?? "DELIVERY") === "DELIVERY" && (
                <div className="space-y-1.5">
                  <select
                    className="w-full rounded-xl border border-white/10 bg-surface-2 px-3 py-2.5 text-sm"
                    value={marketFulfilment[group.sellerId]?.addressId ?? addressId ?? ""}
                    onChange={(e) =>
                      setMarketFulfilment({
                        ...marketFulfilment,
                        [group.sellerId]: {
                          method: "DELIVERY",
                          addressId: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="">Select delivery address</option>
                    {(addresses?.items ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} — {formatAddressLocation(a)}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-white/45">
                    Shipping for this seller
                    {group.shippingFee > 0
                      ? `: ${rm(group.shippingFee)}`
                      : canQuoteShipping
                        ? ": free"
                        : " — select address with state/country"}
                  </p>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      <section className="rounded-2xl border border-white/5 bg-surface p-4 text-xs">
        {hasStoreCart && storeItems.map((item) => (
          <div key={item.id} className="flex items-center gap-2.5 py-1.5">
            <OrderItemThumb
              src={productImageSrc(item.product)}
              alt={item.product.name}
              backgroundColor={item.product.imageBackgroundColor}
              className="h-10 w-10 shrink-0 rounded-lg"
            />
            <ListingTitle as="span" className="min-w-0 flex-1 text-white/70">
              {`${item.product.name} ×${item.quantity}`}
            </ListingTitle>
          </div>
        ))}
        {hasStoreCart && packItems.map((item) => (
          <div key={item.id} className="flex items-center gap-2.5 py-1.5">
            <OrderItemThumb
              src={item.pack.images[0] ?? null}
              alt={item.pack.name}
              className="h-10 w-10 shrink-0 rounded-lg"
            />
            <ListingTitle as="span" className="min-w-0 flex-1 text-white/70">
              {`${item.pack.name} ×${item.quantity}`}
            </ListingTitle>
          </div>
        ))}
        {hasStoreCart && hasMarketCart && (
          <p className="py-1 font-display text-[10px] font-semibold uppercase tracking-widest text-white/35">Mr Gaole</p>
        )}
        {marketGroups.map((group) => (
          <div key={group.sellerId} className="border-t border-white/5 pt-2">
            <p className="text-[10px] font-semibold text-white/50">{group.shopName}</p>
            {group.items.map((row, i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5">
                <OrderItemThumb src={row.listing.images[0] ?? null} alt="" className="h-10 w-10 shrink-0 rounded-lg" />
                <ListingTitle as="span" className="min-w-0 flex-1 text-white/70">
                  {`${row.listing.title} ×${row.quantity}`}
                </ListingTitle>
              </div>
            ))}
          </div>
        ))}
        <div className="mt-2 space-y-1 border-t border-white/10 pt-2">
          {hasStoreCart && (
            <div className="flex justify-between text-white/60">
              <span>Mr Gaole subtotal</span>
              <span>{rm(cart!.subtotal)}</span>
            </div>
          )}
          {hasMarketCart && (
            <div className="flex justify-between text-white/60">
              <span>Items subtotal</span>
              <span>{rm(marketSubtotal)}</span>
            </div>
          )}
          {cart!.discountTotal > 0 && (
            <div className="flex justify-between text-emerald-400">
              <span>Discounts</span>
              <span>−{rm(cart!.discountTotal)}</span>
            </div>
          )}
          {hasMarketCart &&
            marketGroups.map((g) => (
              <div key={g.sellerId} className="flex justify-between text-white/60">
                <span className="truncate pr-2">{g.shopName} shipping</span>
                <span className="shrink-0">{g.shippingFee === 0 && canQuoteShipping ? "FREE" : rm(g.shippingFee)}</span>
              </div>
            ))}
          {shippingQuoted && hasStoreCart && (
            <div className="flex justify-between text-white/60">
              <span>Mr Gaole shipping</span>
              <span>
                {fulfilmentMethod === "PICKUP"
                  ? "FREE (Pickup)"
                  : fulfilmentMethod === "VAULT"
                    ? "FREE (Vault)"
                    : cart!.shippingFee === 0
                    ? "FREE"
                    : rm(cart!.shippingFee)}
              </span>
            </div>
          )}
          {needsShipping && fulfilmentMethod === "DELIVERY" && !canQuoteShipping && (
            <p className="text-[10px] text-white/40">Select a delivery address to see shipping.</p>
          )}
          {needsShipping &&
            fulfilmentMethod === "DELIVERY" &&
            canQuoteShipping &&
            cart!.shippingFee === 0 && (
              <p className="text-[10px] text-emerald-400/90">Free delivery for this address.</p>
            )}
          <div className="flex justify-between pt-1 font-display text-sm font-bold">
            <span>Total</span>
            <span className="text-brand-pink">
              {method === "CREDITS" ? credits(creditCost) : rm(total)}
            </span>
          </div>
        </div>
      </section>

      <PaymentMethodPicker
        methods={methods}
        value={method}
        onChange={setMethod}
        labelForMethod={labelForPaymentMethod}
        isMethodDisabled={(m) =>
          (m === "CREDITS" && (wallet?.balance ?? 0) < creditCost) ||
          (m === PAYMENT_METHOD_FLEXXI && !flexxiKycVerified)
        }
        methodSublabel={(m) => {
          if (m === PAYMENT_METHOD_FLEXXI && !flexxiKycVerified) {
            return "Verify identity to use instalments";
          }
          if (m === "CREDITS" && (wallet?.balance ?? 0) < creditCost) {
            return "Insufficient credits";
          }
          return undefined;
        }}
        methodDetails={(m) => {
          if (m === PAYMENT_METHOD_FLEXXI && flexxiLine && flexxiKycVerified) {
            return (
              <div className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-3">
                <FlexxiShopPanel
                  embedded
                  productId={flexxiLine.productId}
                  quantity={flexxiLine.quantity}
                />
              </div>
            );
          }
          if (m === "BANK_IN") {
            return (
              <BankInTransferDetails
                instructions={config?.bankInInstructions}
                bank={config?.bankInBank}
                accountNumber={config?.bankInAccountNumber}
                accountName={config?.bankInAccountName}
                qrImageUrl={config?.bankInQrImageUrl}
                instructionsFallback="You'll confirm the order next, then upload your transfer receipt on the pending payment page."
              />
            );
          }
          if (m === "STRIPE") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                You&apos;ll be redirected to Stripe to pay by card after you place the order.
              </p>
            );
          }
          if (m === "GKASH") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                You&apos;ll continue on Gkash (FPX or eWallet) after you place the order.
              </p>
            );
          }
          if (m === "CREDITS") {
            return (
              <p className="rounded-xl border border-white/5 bg-surface-2/50 px-3 py-2.5 text-[11px] text-white/50">
                {wallet != null && wallet.balance < creditCost
                  ? "Insufficient credits for this order."
                  : `Wallet will be debited ${credits(creditCost)} when you place the order.`}
              </p>
            );
          }
          return null;
        }}
      />

      {!flexxiCheckout ? (
        <>
          <PolicyConsentCheckbox
            variant="checkout"
            checked={acceptPolicies}
            onChange={setAcceptPolicies}
          />

          <MobileStickyBar active>
            <Button className="w-full" disabled={submitting || !method || !acceptPolicies} onClick={submit}>
              {submitLabel}
            </Button>
          </MobileStickyBar>

          <div className="hidden md:block">
            <Button className="w-full" disabled={submitting || !method || !acceptPolicies} onClick={submit}>
              {submitLabel}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
